import { useEffect, useRef, useState } from "react";
import type { LauncherClient } from "./launcherClient";
import type { FeeQuote, GasEstimate, Hex, RecipientEntry } from "./types";

export interface CreateEstimate {
  quote: FeeQuote | null;
  gas: GasEstimate | null;
  quoteLoading: boolean;
  gasLoading: boolean;
  /**
   * Safe, user-facing reason the last gas estimate failed (null when the
   * estimate succeeded or has not run). Distinguishes an on-chain simulation
   * revert from an RPC transport failure so a revert is never mislabelled as
   * a connectivity problem.
   */
  gasError: string | null;
}

/** Map a thrown estimate failure to a safe user-facing reason. */
function describeEstimateFailure(err: unknown): string {
  const kind = (err as { kind?: string } | null)?.kind;
  if (kind === "simulationRevert") {
    return "The network rejected this launch configuration (simulation reverted). Check the recipient addresses and form values, then try again.";
  }
  if (kind === "rpcError") {
    return "The RPC endpoint did not respond. This is a network issue — it will retry automatically when you change a field or reconnect.";
  }
  return "Gas estimation failed.";
}

/**
 * Re-quotes the fee and re-estimates gas whenever any transaction-affecting input
 * changes. Each new request first CLEARS the previous quote and gas (so a prior
 * wallet's values are never shown) and sets loading. Stale async responses are
 * discarded via a monotonic request id. A failed quote leaves `quote = null`
 * ("unavailable"); a failed/blocked estimate leaves `gas = null` — never 0.
 */
export function useCreateEstimate(
  client: LauncherClient,
  account: Hex,
  name: string,
  symbol: string,
  supplyOk: boolean,
  supplyWhole: bigint | undefined,
  refreshNonce = 0,
  /**
   * Creator reward recipients. When provided, gas is estimated against the
   * ETH-v1 `createMarket` path; when omitted the client falls back to the
   * legacy `createToken` path (which canonical ETH-v1 factories do not
   * implement — so callers deploying via createMarket MUST pass this).
   */
  recipients?: RecipientEntry[]
): CreateEstimate {
  const [quote, setQuote] = useState<FeeQuote | null>(null);
  const [gas, setGas] = useState<GasEstimate | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [gasLoading, setGasLoading] = useState(false);
  const [gasError, setGasError] = useState<string | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    const id = ++reqId.current;
    setQuote(null);
    setGas(null);
    setGasError(null);
    setQuoteLoading(true);
    setGasLoading(supplyOk && supplyWhole !== undefined);

    void (async () => {
      let q: FeeQuote | null = null;
      try {
        q = await client.getFeeQuote(account);
      } catch {
        q = null;
      }
      if (id !== reqId.current) return;
      setQuote(q);
      setQuoteLoading(false);

      if (!(supplyOk && supplyWhole !== undefined)) {
        if (id === reqId.current) {
          setGas(null);
          setGasLoading(false);
        }
        return;
      }
      try {
        let g;
        try {
          g = await client.estimateCreateGas({ name, symbol, supplyWhole, recipients }, account);
        } catch (err) {
          // Retry a genuine transport failure exactly once; never retry a revert.
          if ((err as { kind?: string } | null)?.kind !== "rpcError") throw err;
          g = await client.estimateCreateGas({ name, symbol, supplyWhole, recipients }, account);
        }
        if (id === reqId.current) {
          setGas(g);
          setGasError(null);
        }
      } catch (err) {
        if (id === reqId.current) {
          setGas(null);
          setGasError(describeEstimateFailure(err));
        }
      } finally {
        if (id === reqId.current) setGasLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, account, name, symbol, supplyOk, supplyWhole, refreshNonce, JSON.stringify(recipients ?? null)]);

  return { quote, gas, quoteLoading, gasLoading, gasError };
}
